import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  showAppLoaderSelector,
  userIdSelector,
  isExternalIncompleteUserSelector,
  keycloakConfigSelector,
  keycloakInitStatusSelector
} from '../helpers/selectors';
import { popup_MobileAppSelector_SetVisibility } from '../actions/popup';
import { handleKeycloakLogin } from '../actions/keycloak';
import {
  brandHasKeycloak,
  canUseKeycloakLogin,
  markSentToKeycloakLogin,
  wasSentToKeycloakLogin,
  KEYCLOAK_STATUS
} from '../utils/keycloak';

/**
 * Temporary gate on the web registration (/register/*) routes while registration
 * is being aligned with the mobile Keycloak flow.
 *
 * - A logged-in member (finishing an incomplete profile or adding a company
 *   profile) keeps access — for them the creation flow acts as "complete profile".
 * - A Keycloak-authenticated user still without a member (isExternalIncompleteUser)
 *   keeps access — that path is what the align work will build on.
 * - A visitor Keycloak has answered for, with no session, is sent to the Keycloak
 *   login page: registration now starts there, and he comes back to this same URL
 *   as a Keycloak user without a member, i.e. as the case above.
 * - The "use the mobile app" popup on the home page stays as the fallback for
 *   the visitors we cannot send to Keycloak: a brand with no Keycloak, an init
 *   that failed (so the session is unknown), and anyone already back from a login
 *   that produced no session.
 */
export default function RegisterAccessGuard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // The invite the visitor asked for, and the URL Keycloak will bring him back to
  // (createLoginUrl defaults its redirectUri to location.href). It is what the
  // one-shot marker is keyed by, so one abandoned login only spends that one invite.
  const { pathname } = useLocation();
  const userId = useSelector(userIdSelector);
  const isExternalIncompleteUser = useSelector(isExternalIncompleteUserSelector);
  const showAppLoader = useSelector(showAppLoaderSelector);
  const keycloakConfig = useSelector(keycloakConfigSelector);
  const keycloakInitStatus = useSelector(keycloakInitStatusSelector);

  const allowed = Boolean(userId || isExternalIncompleteUser);
  // "This mount has already asked for a login and is waiting for the answer" - the
  // stored marker cannot play that part, it is only written once the visitor has
  // really left, and the effect can re-run before that.
  const loginRequested = useRef(false);

  useEffect(() => {
    if (allowed) return;

    // Wait for the Keycloak silent check-sso to settle before deciding the visitor
    // is anonymous, otherwise a hard refresh would block before the session loads.
    if (showAppLoader) return;

    const sendHomeWithPopup = () => {
      dispatch(popup_MobileAppSelector_SetVisibility(true));
      navigate('/', { replace: true });
    };

    // The loader going down only means the app stopped waiting: a failed kc.init()
    // ends in ssoDelayLoader, which hides it on a timeout, and at that instant whether
    // the visitor has a session is genuinely unknown. So wait for Keycloak's own
    // answer too - but only where one can still come, since a brand without Keycloak
    // never inits and would leave the visitor here forever.
    if (!brandHasKeycloak(keycloakConfig)) {
      sendHomeWithPopup();
      return;
    }

    if (keycloakInitStatus === null) return;

    // Only Keycloak answering "no session" makes him anonymous. FAIL means it did not
    // answer at all - he may well have a live session, and re-authenticating him with
    // prompt: 'login' would be wrong; AUTH means he has one that led nowhere. Neither
    // is a login worth starting, so both take the fallback.
    if (keycloakInitStatus !== KEYCLOAK_STATUS.UNAUTH) {
      sendHomeWithPopup();
      return;
    }

    // Keycloak brings him back to this exact URL: createLoginUrl defaults its
    // redirectUri to location.href, and /creation/:id is inside this guard, so
    // the invite link he asked for is what he returns to.
    // Once per invite only - a visitor who comes back to it still anonymous (login
    // cancelled, browser Back) would otherwise be bounced out again on arrival,
    // with no way back. Any other invite link in this tab is still free to try.
    if (!wasSentToKeycloakLogin(pathname) && canUseKeycloakLogin(keycloakConfig)) {
      if (loginRequested.current) return;
      loginRequested.current = true;

      // The marker records that he was sent, not that we meant to send him: a login
      // we could not start must leave the next attempt free, and must land him on
      // the fallback instead of on a loader that never comes down.
      dispatch(handleKeycloakLogin(keycloakConfig)).then((sent) => {
        if (sent) markSentToKeycloakLogin(pathname);
        else sendHomeWithPopup();
      });

      return;
    }

    sendHomeWithPopup();
  }, [allowed, showAppLoader, keycloakConfig, keycloakInitStatus, pathname]);

  if (allowed) return <Outlet />;
  return null;
}
